import { useState } from "react";

const faqs = [
  {
    question: "What is the Vigorbuy Spreadsheet?",
    answer: "The Vigorbuy Spreadsheet is a comprehensive product discovery tool that lists thousands of curated items across 14 categories including shoes, hoodies, T-shirts, jackets, bags, and more. Each product listing includes pricing information, quality control details, and direct shopping links to help you make informed purchase decisions.",
  },
  {
    question: "How often is the Vigorbuy Spreadsheet updated?",
    answer: "The Vigorbuy Spreadsheet is updated on a weekly basis with new products, updated pricing, and fresh quality control reviews. Some categories receive daily updates during high-traffic shopping seasons to ensure you always have access to the latest finds and most accurate information.",
  },
  {
    question: "Is the Vigorbuy Spreadsheet free to use?",
    answer: "Yes, access to the Vigorbuy Spreadsheet and all accompanying guides is completely free. We believe in making product discovery accessible to everyone. Simply browse the categories, explore the latest finds, and use our QC guides to shop with confidence.",
  },
  {
    question: "How do I use the Vigorbuy Sheet for shopping?",
    answer: "Start by browsing the 14 product categories on our website. Each category links to a curated list of products on the Vigorbuy Sheet. Use our QC guides to verify product quality before purchasing, and follow our shipping tutorials to understand delivery options and costs. For beginners, we recommend reading the complete Vigorbuy Sheet guide first.",
  },
  {
    question: "Are the products on the Vigorbuy Spreadsheet verified?",
    answer: "Yes, products listed on the Vigorbuy Spreadsheet go through a quality control review process. Each item includes QC documentation where available, helping you spot potential issues before buying. Our team continuously updates product listings to reflect the most current quality assessments.",
  },
  {
    question: "Do you sell products directly?",
    answer: "No, Vigorbuy.blog does not sell products directly. We are an independent shopping guide and product discovery platform. All product links direct you to the main shopping site where you can complete your purchases. Our goal is to provide unbiased information and helpful guides.",
  },
];

export default function FaqSection() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="w-full py-16 md:py-24 bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}></script>
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-5xl font-extrabold text-foreground-950 font-heading tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
            Everything you need to know about the Vigorbuy Spreadsheet and how to use it for smarter shopping
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-lg border border-background-200/60 overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full flex items-center justify-between px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer hover:bg-background-100/50 transition-colors duration-150"
              >
                <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-base text-foreground-400 transition-transform duration-300 ${
                      openIndex === index ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="px-5 md:px-6 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}